import React, { useState, useEffect } from 'react';
import TextClassifier from './components/TextClassifier';
import VoiceClassifier from './components/VoiceClassifier';
import ActionManager from './components/ActionManager';
import ObjectManager from './components/ObjectManager';
import ModifierManager from './components/ModifierManager';
import RelationManager from './components/RelationManager';
import SessionHistory from './components/SessionHistory';
import Analytics from './components/Analytics';
import Settings from './components/Settings';
import CacheManager from './components/CacheManager';
import AuthModal from './components/AuthModal';
import UserManagement from './components/UserManagement';
import StructureManager from './components/StructureManager';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { apiService } from './services/api';
import logger from './utils/logger';

type TabType = 'text' | 'voice' | 'manage' | 'actions' | 'objects' | 'modifiers' | 'relations' | 'history' | 'analytics' | 'settings' | 'cache' | 'users' | 'structures';

// Tabs that require admin role — hidden and blocked for anonymous users and regular 'user' accounts
const ADMIN_TABS = new Set<TabType>([
  'history', 'analytics', 'actions', 'objects', 'modifiers', 'relations', 'cache', 'settings', 'users', 'structures'
]);

function AppInner() {
  const { user, isLoading: authLoading, logout } = useAuth();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('text');

  const isAdmin = user?.role === 'admin';
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [provider, setProvider] = useState<'openai' | 'ollama'>('openai');
  const [ollamaModel, setOllamaModel] = useState<string>('gpt-oss:20b');
  const [openaiModel, setOpenaiModel] = useState<string>(() => {
    // Try to load from localStorage, default to gpt-4o-mini
    return localStorage.getItem('openai-model') || 'gpt-4o-mini';
  });
  const [useQueryEmbedding, setUseQueryEmbedding] = useState<boolean>(true);
  const [useCache, setUseCache] = useState<boolean>(true);
  const [useMemory, setUseMemory] = useState<boolean>(true);

  // Load settings from database on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await apiService.getSettings();
        setProvider(settings.ai_provider);
        setUseQueryEmbedding(settings.use_query_embedding);
        setUseCache(settings.use_cache);
        setUseMemory(settings.use_memory ?? true);
        logger.info('Settings loaded from database:', settings);
      } catch (error) {
        logger.error('Failed to load settings from database:', error);
        // Use defaults if loading fails
        setProvider('openai');
        setUseQueryEmbedding(true);
        setUseCache(true);
        setUseMemory(true);
      }
    };

    loadSettings();
  }, []);

  // Save OpenAI model to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('openai-model', openaiModel);
    logger.info('OpenAI model saved to localStorage:', openaiModel);
  }, [openaiModel]);

  // Redirect away from admin-only tabs when the user loses admin access
  // (on logout, or if a non-admin user somehow ends up on one)
  useEffect(() => {
    if (!authLoading && !isAdmin && ADMIN_TABS.has(activeTab)) {
      setActiveTab('text');
    }
  }, [isAdmin, authLoading, activeTab]);

  // Initialize session on app start
  useEffect(() => {
    const initSession = async () => {
      try {
        const session = await apiService.createSession();
        setSessionId(session.sessionId);
        logger.info('Session created:', session.sessionId);
      } catch (error) {
        logger.error('Failed to create session:', error);
      } finally {
        setSessionLoading(false);
      }
    };

    initSession();
  }, []);

  // Create new session
  const handleNewSession = async () => {
    try {
      setSessionLoading(true);
      if (sessionId) {
        // Close current session
        await apiService.closeSession(sessionId);
      }
      // Create new session
      const session = await apiService.createSession();
      setSessionId(session.sessionId);
      logger.info('New session created:', session.sessionId);
    } catch (error) {
      logger.error('Failed to create new session:', error);
    } finally {
      setSessionLoading(false);
    }
  };

  // Close current session
  const handleCloseSession = async () => {
    if (sessionId) {
      try {
        await apiService.closeSession(sessionId);
        setSessionId(null);
        logger.info('Session closed');
      } catch (error) {
        logger.error('Failed to close session:', error);
      }
    }
  };

  const allTabs = [
    { id: 'text',      label: 'Text Classification',      icon: '📝', adminOnly: false },
    { id: 'voice',     label: 'Voice Classification',     icon: '🎤', adminOnly: false },
    { id: 'history',   label: 'Session History',          icon: '📋', adminOnly: true  },
    { id: 'analytics', label: 'User Analytics',           icon: '📊', adminOnly: true  },
    { id: 'actions',   label: 'Manage Actions',           icon: '🎯', adminOnly: true  },
    { id: 'objects',   label: 'Manage Objects',           icon: '📦', adminOnly: true  },
    { id: 'modifiers', label: 'Manage Modifiers',         icon: '🔧', adminOnly: true  },
    { id: 'relations', label: 'Action-Object Relations',  icon: '🔗', adminOnly: true  },
    { id: 'structures',  label: 'Manage Structures',      icon: '🦴', adminOnly: true  },
    { id: 'cache',     label: 'Admin Cache Data',         icon: '💾', adminOnly: true  },
    { id: 'users',       label: 'User Management',        icon: '👥', adminOnly: true  },
    { id: 'settings',    label: 'Settings',               icon: '⚙️', adminOnly: true  },
  ];

  const tabs = allTabs.filter(tab => !tab.adminOnly || isAdmin);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <h1 className="text-3xl font-bold text-gray-900">
                Intent Classification
              </h1>
              <span className="ml-3 text-sm text-gray-500">
                Powered by AI
              </span>
            </div>
            <div className="flex items-center space-x-4">
              {/* Session Controls */}
              {sessionLoading ? (
                <span className="text-sm text-gray-500">Loading session...</span>
              ) : sessionId ? (
                <>
                  <span className="text-sm text-gray-600">
                    Session: <span className="font-mono text-xs">{sessionId.slice(0, 8)}...</span>
                  </span>
                  <button
                    onClick={handleNewSession}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                  >
                    New Session
                  </button>
                  <button
                    onClick={handleCloseSession}
                    className="px-3 py-1 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors"
                  >
                    Close Session
                  </button>
                </>
              ) : (
                <button
                  onClick={handleNewSession}
                  className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
                >
                  Start Session
                </button>
              )}

              {/* Auth Controls */}
              <div className="border-l border-gray-200 pl-4">
                {authLoading ? null : user ? (
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold ${isAdmin ? 'bg-purple-600' : 'bg-blue-600'}`}>
                        {user.username.charAt(0).toUpperCase()}
                      </div>
                      <div className="text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{user.name || user.username}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${isAdmin ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                            {user.role}
                          </span>
                        </div>
                        {user.institution && (
                          <div className="text-xs text-gray-500">{user.institution}</div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={logout}
                      className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      Sign Out
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">Anonymous</span>
                    <button
                      onClick={() => setAuthModalOpen(true)}
                      className="px-3 py-1 bg-gray-900 text-white text-sm rounded-md hover:bg-gray-700 transition-colors"
                    >
                      Sign In
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} />

      {/* Sidebar and Content Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar Navigation */}
        <nav className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
          <div className="flex flex-col py-4">
            {/* User tabs — always visible */}
            {tabs.filter(t => !t.adminOnly).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`flex items-center px-6 py-3 text-sm font-medium transition-colors duration-200 ${
                  activeTab === tab.id
                    ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-700'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 border-l-4 border-transparent'
                }`}
              >
                <span className="text-xl mr-3">{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}

            {/* Admin tabs — only rendered for admin role */}
            {isAdmin && (
              <>
                <div className="mx-6 my-2 border-t border-gray-200" />
                <p className="px-6 pt-1 pb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Admin
                </p>
                {tabs.filter(t => t.adminOnly).map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as TabType)}
                    className={`flex items-center px-6 py-3 text-sm font-medium transition-colors duration-200 ${
                      activeTab === tab.id
                        ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-700'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 border-l-4 border-transparent'
                    }`}
                  >
                    <span className="text-xl mr-3">{tab.icon}</span>
                    <span>{tab.label}</span>
                  </button>
                ))}
              </>
            )}
          </div>
        </nav>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Hard guard: if a non-admin somehow lands on an admin tab, show access denied */}
          {ADMIN_TABS.has(activeTab) && !isAdmin ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="text-5xl mb-4">🔒</div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Admin Access Required</h2>
              <p className="text-gray-500 text-sm max-w-sm">
                This section is only available to admin accounts.
                {!user && <> <button onClick={() => setAuthModalOpen(true)} className="text-blue-600 hover:underline ml-1">Sign in</button> with an admin account to continue.</>}
              </p>
            </div>
          ) : activeTab === 'actions' ? (
            <ActionManager />
          ) : activeTab === 'objects' ? (
            <ObjectManager />
          ) : activeTab === 'modifiers' ? (
            <ModifierManager />
          ) : activeTab === 'relations' ? (
            <RelationManager />
          ) : activeTab === 'history' ? (
            <SessionHistory />
          ) : activeTab === 'analytics' ? (
            <Analytics />
          ) : activeTab === 'cache' ? (
            <CacheManager />
          ) : activeTab === 'users' ? (
            <UserManagement />
          ) : activeTab === 'structures' ? (
            <StructureManager />
          ) : activeTab === 'settings' ? (
            <Settings 
              provider={provider} 
              setProvider={setProvider}
              ollamaModel={ollamaModel}
              setOllamaModel={setOllamaModel}
              openaiModel={openaiModel}
              setOpenaiModel={setOpenaiModel}
              useQueryEmbedding={useQueryEmbedding}
              setUseQueryEmbedding={setUseQueryEmbedding}
              useCache={useCache}
              setUseCache={setUseCache}
              useMemory={useMemory}
              setUseMemory={setUseMemory}
            />
          ) : sessionId ? (
            <>
              {activeTab === 'text' && <TextClassifier sessionId={sessionId} provider={provider} ollamaModel={ollamaModel} openaiModel={openaiModel} />}
              {activeTab === 'voice' && <VoiceClassifier sessionId={sessionId} provider={provider} ollamaModel={ollamaModel} openaiModel={openaiModel} />}
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">Please start a session to begin classification.</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}

export default App;
